import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createSelector } from 'reselect';
import { useDispatch, shallowEqual, useSelector } from 'react-redux';
import {
  Wrapper,
  StandardWrapper,
  ActivityHeaderWrapper,
  TopicName,
  Icon,
  Content,
  Info,
  FixedWrapper,
  CollapsedWrapper
} from './styled';
import {
  StandardDropdown,
  CollapseButton
} from '../../helpers/icon-dictionary';
import { getContextCoords, isDefined } from '../../helpers/utils';
import SvgIcon from '../svg-icon/svg-icon';
import ActivityHeader from '../activity-header';
import ContextModal, { ContextModalWidth } from '../context-modal';
import TopicInfo from '../topic-info';
import OutsideClickHandler from '../../helpers/hoc/close/outside-click-handler';
import TopicCollapsed from './collapsed';
import CollapsedGrid from '../grid/collapsed';
import Grid from '../grid';
import {
  DISPLAY_MODAL_INFORMATION,
  HIDE_STANDARD,
  SET_TOPIC_TO_DIRTY
} from '../../containers/course-assign/course-assign-types';

const Topic = ({
  id,
  topicName,
  i18n,
  collapseAllStandards,
  isTopicDirty,
  activities
}) => {
  const iconRef = useRef(null);
  const infoRef = useRef(null);
  const dispatch = useDispatch();

  const [showContextMenu, setContextMenu] = useState(false);
  const [showCallOutMenu, setCallOutMenu] = useState(false);
  const [positionX, setPositionX] = useState(0);
  const [positionY, setPositionY] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(collapseAllStandards);
  const [positionArrow, setPositionArrow] = useState(0);
  const [positionXCallout, setPositionXCallout] = useState(0);
  const [positionYCallout, setPositionYCallout] = useState(0);
  const [positionArrowCallout, setPositionArrowCallout] = useState(0);
  const [contextTheme, setContextTheme] = useState(null);

  useEffect(() => {
    if (!isTopicDirty) {
      setIsCollapsed(collapseAllStandards);
    }
  }, [collapseAllStandards, isTopicDirty]);

  const mappedAssignmentsSelector = createSelector(
    (state) => state.CourseAssignReducer?.assignments?.studentAssignments || [],
    () => activities,
    (studentAssignments, selectorActivities) => {
      const nameObj = {};
      selectorActivities.forEach((activity) => {
        studentAssignments.forEach((studentAssignment, index) => {
          if (
            studentAssignment.activityId === activity.activityId &&
            studentAssignment.taskType === activity.taskType
          ) {
            const nameArr = nameObj[activity.activityName];
            if (isDefined(nameArr)) {
              nameObj[activity.activityName].push(index);
            } else {
              nameObj[activity.activityName] = [index];
            }
          }
        });
      });
      return nameObj;
    }
  );

  const mappedAssignmentIndexes = useSelector(
    mappedAssignmentsSelector,
    shallowEqual
  );

  const showDropdown = () => {
    const { posX, posY, posArrow } = getContextCoords(
      iconRef.current.getBoundingClientRect(),
      ContextModalWidth
    );
    setPositionY(posY);
    setPositionX(posX);
    setPositionArrow(posArrow);
    setContextTheme(null);
    setCallOutMenu(false);
    setContextMenu(false);
  };

  const hideStandardBlock = () => {
    dispatch({
      type: HIDE_STANDARD,
      id
    });
    dispatch({
      type: DISPLAY_MODAL_INFORMATION,
      key: -1
    });
  };

  const handleExpandCollapse = (state) => {
    dispatch({
      type: SET_TOPIC_TO_DIRTY
    });
    setIsCollapsed(state);
  };

  const showCallOut = (e) => {
    const { posX, posArrow } = getContextCoords(e, ContextModalWidth);
    const { bottom } = infoRef.current.getBoundingClientRect();
    setPositionYCallout(bottom + 12);
    setPositionXCallout(posX);
    setPositionArrowCallout(posArrow);
    setContextTheme('standard');
    setCallOutMenu(true);
  };

  const hideCallOut = () => {
    setCallOutMenu(false);
  };

  const ActivityHeaders = useMemo(
    () =>
      activities &&
      activities.map((activity) => (
        <ActivityHeader
          externalKey={topicName}
          i18n={i18n}
          activity={activity}
          studentAssignmentIndexes={
            mappedAssignmentIndexes?.[activity.activityName]
          }
          key={activity.activityName}
        />
      )),
    // studentAssignmentIndexes will change each time, causing expensive rendering
    // using only activities for dependency is fine, as when activities change the studentAssignmentIndexes will change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activities, i18n, topicName]
  );

  if (isCollapsed) {
    return (
      <CollapsedWrapper>
        <TopicCollapsed
          handleOnExpand={() => handleExpandCollapse(false)}
          topicName={topicName}
          id={id}
        />
        <CollapsedGrid topicId={id} i18n={i18n} activities={activities} />
      </CollapsedWrapper>
    );
  }

  return (
    <Wrapper>
      <FixedWrapper>
        <StandardWrapper
          activities={activities.length}
          className="standard"
          id="showContextModal"
        >
          <Icon className="leftAlign" ref={iconRef}>
            <SvgIcon icon={StandardDropdown} />
          </Icon>
          <Info
            role="button"
            tabIndex="-1"
            onClick={showDropdown}
            activities={activities.length}
            onMouseEnter={showCallOut}
            onMouseLeave={hideCallOut}
            ref={infoRef}
          >
            <Content>
              <TopicName>{topicName}</TopicName>
            </Content>
            <Icon
              role="button"
              tabIndex="-1"
              onClick={() => handleExpandCollapse(true)}
            >
              <SvgIcon icon={CollapseButton} />
            </Icon>
          </Info>
        </StandardWrapper>
        <ActivityHeaderWrapper>{ActivityHeaders}</ActivityHeaderWrapper>
      </FixedWrapper>
      <Grid
        i18n={i18n}
        activities={activities}
        mappedAssignmentIndexes={mappedAssignmentIndexes}
      />
      <OutsideClickHandler
        callback={() => setContextMenu(false)}
        forButton={`showContextModal_${id}`}
      >
        <ContextModal
          show={showContextMenu}
          positionArrow={positionArrow}
          positionX={positionX}
          positionY={positionY}
        >
          <TopicInfo
            standardName={topicName}
            hideStrand={hideStandardBlock}
            displayHideOption
          />
        </ContextModal>
        <ContextModal
          show={showCallOutMenu}
          positionArrow={positionArrowCallout}
          positionX={positionXCallout}
          positionY={positionYCallout}
          contextTheme={contextTheme}
          name={topicName}
        />
      </OutsideClickHandler>
    </Wrapper>
  );
};

Topic.propTypes = {
  id: PropTypes.number,
  topicName: PropTypes.string.isRequired,
  activities: PropTypes.array.isRequired,
  i18n: PropTypes.object,
  collapseAllStandards: PropTypes.bool,
  isTopicDirty: PropTypes.bool.isRequired
};

Topic.defaultProps = {
  externalKey: ''
};

Topic.whyDidYouRender = false;

export default Topic;



// WEBPACK FOOTER //
// ./components/topic/index.js